import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useAppSelector } from '@customer/store/hooks';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import {
  Control,
  FieldValues,
  Path,
  useController,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

interface useGetTimeSelectionWindowProps {
  days: number;
  interval: number;
}

export const useGetTimeSelectionWindow = ({
  days,
  interval,
}: useGetTimeSelectionWindowProps): [DateTime, DateTime] => {
  const timezone = useAppSelector(state => state.auth.siteTimezone);
  const currentTime = DateTime.now().setZone(timezone);

  const nearestPreceding = currentTime.set({
    minute: currentTime.minute - (currentTime.minute % interval),
    second: 0,
    millisecond: 0,
  });

  const furthest = nearestPreceding.minus({ days });

  return [nearestPreceding, furthest];
};

interface GetStartDaysProps {
  nearest: DateTime;
  furthest: DateTime;
}

const getStartDays = ({ nearest, furthest }: GetStartDaysProps): string[] => {
  const startDays: string[] = [];

  // Note: The following minus operation ignores DST unless the DateTime object includes a timezone (e.g. Australia/Sydney).
  // The ISO string that is used to interface with the API uses a timezone offset, which is insufficient.
  for (let dt = nearest; dt >= furthest; dt = dt.minus({ days: 1 })) {
    startDays.push(dt.startOf('day').toString());
  }

  return startDays;
};

export const useStartDayOptions = ({
  nearest,
  furthest,
}: GetStartDaysProps): DropdownItemProps<string>[] => {
  const { showDateOnlyFromIso } = useLocaleFormatter();

  const startDayOptions = useMemo<DropdownItemProps<string>[]>(() => {
    const startDays = getStartDays({ nearest, furthest });
    return startDays.map(startDay => {
      return {
        key: startDay,
        value: startDay,
        stringValue: startDay,
        label: showDateOnlyFromIso(startDay),
      };
    });
  }, [nearest, furthest, showDateOnlyFromIso]);

  return startDayOptions;
};

interface GetStartDateTimesProps<T> extends GetStartDaysProps {
  startDay: T;
  interval: number;
}

const getStartDateTimes = ({
  startDay, // Must be a DateTime object with a timezone.
  nearest,
  furthest,
  interval,
}: GetStartDateTimesProps<DateTime>): string[] => {
  // To preserve round time intervals, must not use endOf in calculations.
  const endOfDay = startDay.plus({ days: 1 }).minus({ minutes: interval });
  const furthestTimeOfDay = DateTime.max(startDay, furthest);
  const nearestTimeOfDay = DateTime.min(endOfDay, nearest);

  const startDateTimes: string[] = [];

  for (let dt = nearestTimeOfDay; dt >= furthestTimeOfDay; dt = dt.minus({ minutes: interval })) {
    startDateTimes.push(dt.toString());
  }

  return startDateTimes;
};

export const useStartDateTimeOptions = ({
  startDay: startDayValue,
  nearest,
  furthest,
  interval,
}: GetStartDateTimesProps<string | null>): DropdownItemProps<string>[] => {
  const { showTimeOnlyFromIso } = useLocaleFormatter();
  const timezone = useAppSelector(state => state.auth.siteTimezone);

  const startDateTimeOptions = useMemo<DropdownItemProps<string>[]>(() => {
    if (startDayValue !== null) {
      const startDay = DateTime.fromISO(startDayValue).setZone(timezone);
      const startDateTimes = getStartDateTimes({ startDay, nearest, furthest, interval });

      return startDateTimes.map(startDateTime => ({
        key: startDateTime,
        value: startDateTime,
        stringValue: startDateTime,
        label: showTimeOnlyFromIso(startDateTime, true),
      }));
    } else {
      return [];
    }
  }, [startDayValue, nearest, furthest, interval, showTimeOnlyFromIso, timezone]);

  return startDateTimeOptions;
};

interface UseSyncFieldWithOptionsProps<
  TInput extends FieldValues,
  TOutput extends TInput = TInput,
> {
  fieldName: Path<TInput>;
  options: DropdownItemProps<string>[];
  control: Control<TInput, undefined, TOutput>;
  trigger: UseFormTrigger<TInput>;
}

const useSyncFieldWithOptions = <TInput extends FieldValues, TOutput extends TInput = TInput>({
  fieldName,
  options,
  control,
  trigger,
}: UseSyncFieldWithOptionsProps<TInput, TOutput>) => {
  const { field } = useController({ control, name: fieldName });
  const isInOptions = field.value === null || options.some(option => option.value === field.value);

  // Clear the field if the provided value is not in the list of options.
  useEffect(() => {
    if (!isInOptions) {
      field.onChange(null);
      trigger(fieldName); // Use trigger instead of 'onBlur' (trigger is guaranteed to execute).
    }
  }, [isInOptions, field, fieldName, trigger]);
};

interface UseStartTimeFieldsProps<TInput extends FieldValues, TOutput extends TInput = TInput>
  extends useGetTimeSelectionWindowProps {
  startDateFieldName: Path<TInput>;
  startTimeFieldName: Path<TInput>;
  control: Control<TInput, undefined, TOutput>;
  watch: UseFormWatch<TInput>;
  trigger: UseFormTrigger<TInput>;
}

interface UseStartTimeFieldsReturn {
  startDayOptions: DropdownItemProps<string>[];
  startDateTimeOptions: DropdownItemProps<string>[];
}

export const useStartTimeFields = <TInput extends FieldValues, TOutput extends TInput = TInput>({
  days,
  interval,
  control,
  watch,
  trigger,
  startDateFieldName,
  startTimeFieldName,
}: UseStartTimeFieldsProps<TInput, TOutput>): UseStartTimeFieldsReturn => {
  const [nearest, furthest] = useGetTimeSelectionWindow({
    days,
    interval,
  });

  const startDayOptions = useStartDayOptions({ nearest, furthest });

  const selectedDay = watch(startDateFieldName);

  useSyncFieldWithOptions({
    fieldName: startDateFieldName,
    options: startDayOptions,
    control,
    trigger,
  });

  const startDateTimeOptions = useStartDateTimeOptions({
    startDay: selectedDay,
    nearest,
    furthest,
    interval,
  });

  useSyncFieldWithOptions({
    fieldName: startTimeFieldName,
    options: startDateTimeOptions,
    control,
    trigger,
  });

  // Trigger validation of the start time field whenever the day changes to a non-null value.
  useEffect(() => {
    if (selectedDay !== null) {
      trigger([startTimeFieldName]);
    }
  }, [selectedDay, startTimeFieldName, trigger]);

  return {
    startDayOptions,
    startDateTimeOptions,
  };
};
